import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type {
  AdminRole,
  AdminUserDto,
  BusinessSettings,
  UpdateBusinessSettings,
} from '@mys/contracts';
import { ADMIN_ROLES, ADMIN_ROLE_LABELS } from '@mys/contracts/constants';
import { ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useCan } from '../lib/permissions.js';
import { formatDateTime } from '../lib/format.js';
import {
  ACCOUNT_STATE_LABELS,
  accountState,
  invoiceReady,
  missingForInvoice,
  useBusinessSettings,
  useChangePassword,
  useInvite,
  useReinvite,
  useTeam,
  useUpdateBusinessSettings,
  useUpdateMe,
  useUpdateTeamMember,
} from '../lib/settings.js';
import { sortRoles, useRoleMatrix } from '../lib/roles.js';
import {
  downloadRecoveryCodes,
  useBeginEnrolment,
  useConfirmEnrolment,
  useDisableTwoFactor,
  useTwoFactor,
  type Enrolment,
} from '../lib/twoFactor.js';

/* ============================================================================
   /settings — the team, your own account, and what goes on an invoice.
   ----------------------------------------------------------------------------
   Three sections, two audiences. Your own account is yours; the team and the
   business details are the owner's, and staff do not see those sections at
   all. The API is the gate either way — `RequireRole` and this are a courtesy,
   and anyone can edit the JavaScript running on their own machine.
   ========================================================================== */

export function SettingsPage() {
  const { user } = useSession();
  const may = useCan();
  const mayTeam = may('settings:team');
  const mayBusiness = may('settings:business');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p className="muted">
            {mayTeam
              ? 'The team, your account, and the details printed on invoices.'
              : 'Your account.'}
          </p>
        </div>
      </div>

      <div className="stack">
        <MyAccount />
        {/* Everyone, not just the owner. A staff account reaches leads, clients
            and every client's phone number; "only the owner needs a second
            factor" is a claim about who is worth attacking, and it is wrong. */}
        <TwoFactorSection />
        {mayBusiness ? <BusinessDetails /> : null}
        {mayTeam && user ? <Team currentUserId={user.id} /> : null}
      </div>
    </>
  );
}

/* --- your own account ------------------------------------------------------ */

function MyAccount() {
  const { user, refresh } = useSession();
  const updateMe = useUpdateMe();
  const changePassword = useChangePassword();

  const [name, setName] = useState(user?.name ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');

  function onSaveName(event: FormEvent) {
    event.preventDefault();
    updateMe.mutate(
      { name: name.trim() },
      {
        // The name is in the shell's footer and the overview's greeting, so the
        // session has to learn about it or the change looks like it failed.
        onSuccess: () => void refresh(),
      },
    );
  }

  function onChangePassword(event: FormEvent) {
    event.preventDefault();
    changePassword.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setCurrent('');
          setNext('');
        },
      },
    );
  }

  return (
    <section className="card">
      <h2 className="section-title">Your account</h2>

      <form className="stack" onSubmit={onSaveName} noValidate>
        <div className="field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" value={user?.email ?? ''} disabled readOnly />
          {/* Not editable, and the reason is worth saying out loud on screen:
              this address is the name against every lead note, settled invoice
              and project change this person has made. */}
          <span className="muted" style={{ fontSize: 13 }}>
            The email cannot be changed — it is how you are named on every record.
          </span>
        </div>

        <Problem error={updateMe.error} />

        <div className="row">
          <button className="btn" type="submit" disabled={updateMe.isPending}>
            {updateMe.isPending ? 'Saving…' : 'Save name'}
          </button>
          {updateMe.isSuccess && !updateMe.isPending ? (
            <span className="muted" role="status">
              Saved.
            </span>
          ) : null}
        </div>
      </form>

      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '20px 0' }} />

      <form className="stack" onSubmit={onChangePassword} noValidate>
        <h3 style={{ fontSize: 16, margin: 0 }}>Password badliye</h3>

        <div className="field">
          <label htmlFor="currentPassword">Current password</label>
          <input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="newPassword">New password</label>
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            At least 12 characters. Length is what helps — special characters are not required.
          </span>
        </div>

        <Problem error={changePassword.error} />

        <div className="row">
          <button
            className="btn"
            type="submit"
            disabled={changePassword.isPending || !current || !next}
          >
            {changePassword.isPending ? 'Saving…' : 'Change password'}
          </button>
          {changePassword.isSuccess && !changePassword.isPending ? (
            <span className="muted" role="status">
              Password changed. Every other session has been signed out.
            </span>
          ) : null}
        </div>

        {/* Said BEFORE they press it, not only after. Somebody changing their
            password is usually worried about a phone they left somewhere, and
            whether this handles that is the question they came with. */}
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Changing your password signs out every other browser and phone by itself. This one stays
          signed in.
        </p>
      </form>
    </section>
  );
}

/* --- two-factor authentication --------------------------------------------- */

/**
 * Four states, and they are not one screen with bits hidden.
 *
 *   unavailable  the server has no TOTP_ENCRYPTION_KEY, so nothing can be
 *                stored. Said out loud rather than offering a button that
 *                fails at its last step.
 *   off          an explanation and one button
 *   enrolling    the QR, the typed key, and a code to prove it works
 *   on           when, how many codes are left, and how to turn it off
 */
function TwoFactorSection() {
  const { user } = useSession();
  const status = useTwoFactor();
  const begin = useBeginEnrolment();
  const confirm = useConfirmEnrolment();
  const disable = useDisableTwoFactor();

  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  /* Held in memory and shown once. Deliberately not persisted anywhere: these
     open the account, and a dashboard that could re-display them would make
     "shown once" a false promise. */
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  function onBegin() {
    begin.mutate(undefined, { onSuccess: (data) => setEnrolment(data) });
  }

  function onConfirm(event: FormEvent) {
    event.preventDefault();
    confirm.mutate(code, {
      onSuccess: (data) => {
        setRecoveryCodes(data.recoveryCodes);
        setEnrolment(null);
        setCode('');
      },
    });
  }

  function onDisable(event: FormEvent) {
    event.preventDefault();
    disable.mutate(password, {
      onSuccess: () => {
        setPassword('');
        setRecoveryCodes(null);
      },
    });
  }

  if (status.isPending) return null;
  const twoFactor = status.data;

  return (
    <section className="card stack">
      <h2>Two-factor authentication</h2>

      {twoFactor && !twoFactor.available ? (
        <p className="alert" role="status">
          Two-factor authentication is not configured on this server, so it cannot be turned on yet.{' '}
          <code>TOTP_ENCRYPTION_KEY</code> has to be set first.
        </p>
      ) : null}

      {recoveryCodes ? (
        <RecoveryCodes
          codes={recoveryCodes}
          email={user?.email ?? ''}
          onDone={() => setRecoveryCodes(null)}
        />
      ) : null}

      {enrolment ? (
        <form className="stack" onSubmit={onConfirm} noValidate>
          <p className="muted" style={{ margin: 0 }}>
            Scan this with Google Authenticator, Authy, 1Password or any authenticator app, then
            type the six-digit code it shows.
          </p>

          {/* An <img>, not injected markup: no browser runs anything inside an
              SVG loaded this way, and `img-src 'self' data:` is already in the
              dashboard's CSP. The white background is not decoration — a QR
              inverted by the dark theme does not scan. */}
          <img
            src={enrolment.qrSvgDataUri}
            alt="QR code for setting up two-factor authentication"
            width={200}
            height={200}
            style={{ background: '#fff', padding: 8, borderRadius: 8, alignSelf: 'flex-start' }}
          />

          <div className="field">
            <label htmlFor="totp-secret">Or type this key in by hand</label>
            <input
              id="totp-secret"
              readOnly
              value={enrolment.secret}
              onFocus={(e) => e.target.select()}
            />
          </div>

          <div className="field">
            <label htmlFor="totp-code">Six-digit code</label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {confirm.error ? <span className="field__error">{confirm.error.message}</span> : null}
          </div>

          <div className="row">
            <button className="btn" type="submit" disabled={confirm.isPending}>
              {confirm.isPending ? 'Checking…' : 'Turn it on'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setEnrolment(null);
                setCode('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {!enrolment && twoFactor?.enabled ? (
        <>
          <p style={{ margin: 0 }}>
            <span className="pill">On</span>{' '}
            <span className="muted">
              since {twoFactor.confirmedAt ? formatDateTime(twoFactor.confirmedAt) : 'recently'} ·{' '}
              {twoFactor.recoveryCodesLeft} recovery{' '}
              {twoFactor.recoveryCodesLeft === 1 ? 'code' : 'codes'} left
            </span>
          </p>

          {twoFactor.recoveryCodesLeft === 0 ? (
            <p className="alert" role="status">
              You have no recovery codes left. If you lose your phone now, only the owner can get
              you back in. Turn two-factor off and on again for a fresh set.
            </p>
          ) : null}

          <form className="stack" onSubmit={onDisable} noValidate>
            <div className="field">
              <label htmlFor="disable-2fa-password">Turn it off — confirm your password</label>
              <input
                id="disable-2fa-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {disable.error ? <span className="field__error">{disable.error.message}</span> : null}
            </div>
            <button className="btn btn--ghost" type="submit" disabled={disable.isPending}>
              {disable.isPending ? 'Turning off…' : 'Turn off two-factor'}
            </button>
          </form>
        </>
      ) : null}

      {!enrolment && twoFactor && !twoFactor.enabled && twoFactor.available ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Ask for a six-digit code from your phone as well as your password. A password that
            leaks, or one reused somewhere else, stops being enough on its own.
          </p>
          {begin.error ? (
            <p className="alert" role="alert">
              {begin.error.message}
            </p>
          ) : null}
          <button className="btn" type="button" onClick={onBegin} disabled={begin.isPending}>
            {begin.isPending ? 'Setting up…' : 'Set up two-factor'}
          </button>
        </>
      ) : null}
    </section>
  );
}

/**
 * The codes, shown once.
 *
 * The download button is the point of this component. "Save these somewhere
 * safe" is advice people mean to follow in a minute, and the tab closes first.
 * A file is the one version of that which survives a closed tab.
 */
function RecoveryCodes({
  codes,
  email,
  onDone,
}: {
  codes: string[];
  email: string;
  onDone: () => void;
}) {
  return (
    <div className="stack">
      <p className="alert alert--ok" role="status">
        Two-factor is on. Save these recovery codes now — this is the only time they are shown.
      </p>
      <ul
        className="stack"
        style={{
          gap: 4,
          margin: 0,
          padding: 12,
          listStyle: 'none',
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Each works once. Anyone holding one of these and your password can sign in, so keep them
        where you would keep a spare key.
      </p>
      <div className="row">
        <button className="btn" type="button" onClick={() => downloadRecoveryCodes(codes, email)}>
          Download
        </button>
        <button className="btn btn--ghost" type="button" onClick={onDone}>
          I have saved them
        </button>
      </div>
    </div>
  );
}

/* --- what goes on an invoice ----------------------------------------------- */

type BusinessFields = Omit<BusinessSettings, 'updatedAt' | 'updatedByName'>;

const BLANK: BusinessFields = {
  legalName: null,
  gstin: null,
  addressLine: null,
  city: null,
  state: null,
  pincode: null,
  bankName: null,
  accountName: null,
  accountNumber: null,
  ifsc: null,
  upiId: null,
};

const FIELD_LABELS: Record<keyof BusinessFields, string> = {
  legalName: 'Full legal name',
  gstin: 'GSTIN',
  addressLine: 'Address',
  city: 'City',
  state: 'State',
  pincode: 'PIN code',
  bankName: 'Bank',
  accountName: 'Account holder name',
  accountNumber: 'Account number',
  ifsc: 'IFSC',
  upiId: 'UPI id',
};

const INVOICE_FIELDS: (keyof BusinessFields)[] = [
  'legalName',
  'gstin',
  'addressLine',
  'city',
  'state',
  'pincode',
];
const BANK_FIELDS: (keyof BusinessFields)[] = [
  'bankName',
  'accountName',
  'accountNumber',
  'ifsc',
  'upiId',
];

function BusinessDetails() {
  const { data, isPending, error } = useBusinessSettings();
  const save = useUpdateBusinessSettings();

  const [fields, setFields] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Once, so a refetch cannot overwrite what is half-typed.
    if (loaded || !data) return;
    const next: Record<string, string> = {};
    for (const key of Object.keys(BLANK)) {
      next[key] = (data.settings as Record<string, unknown>)[key]?.toString() ?? '';
    }
    setFields(next);
    setLoaded(true);
  }, [data, loaded]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    /* Empty means "clear this", which the API reads as null. Every field here
       is clearable rather than optional for exactly that reason: a GSTIN typed
       wrong has to be removable, and in a PATCH `undefined` has to keep
       meaning "not sent". */
    const changes: Record<string, string | null> = {};
    for (const key of Object.keys(BLANK)) {
      const value = (fields[key] ?? '').trim();
      changes[key] = value === '' ? null : value;
    }
    save.mutate(changes as UpdateBusinessSettings);
  }

  if (isPending) {
    return (
      <section className="card">
        <h2 className="section-title">Invoice details</h2>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="card">
        <h2 className="section-title">Invoice details</h2>
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the details.'}
        </p>
      </section>
    );
  }

  const settings = data.settings;
  const missing = missingForInvoice(settings);

  const fieldError = (name: string): string | undefined =>
    save.error instanceof ApiError ? save.error.fields[name] : undefined;

  return (
    <section className="card">
      <h2 className="section-title">Invoice details</h2>

      {/* Said before anything is filled in, not discovered at the moment
          somebody needs an invoice. The GSTIN in particular is still a real
          blocker — see the placeholders list in CONTEXT.md §13. */}
      {invoiceReady(settings) ? (
        <p className="muted" style={{ marginTop: 0 }}>
          Everything an invoice needs is filled in.
        </p>
      ) : (
        <p className="alert" role="status">
          A GST invoice still needs {missing.join(', ')}. Until these are filled in, that space on
          the invoice stays blank — blank is better than printing the wrong number.
        </p>
      )}

      <form className="stack" onSubmit={onSubmit} noValidate>
        {INVOICE_FIELDS.map((key) => (
          <Field
            key={key}
            name={key}
            label={FIELD_LABELS[key]}
            value={fields[key] ?? ''}
            error={fieldError(key)}
            onChange={(value) => setFields((current) => ({ ...current, [key]: value }))}
          />
        ))}

        <h3 style={{ fontSize: 16, margin: '8px 0 0' }}>Where the money arrives</h3>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          This is printed on the invoice and is what gets sent to a client on WhatsApp.
        </p>

        {BANK_FIELDS.map((key) => (
          <Field
            key={key}
            name={key}
            label={FIELD_LABELS[key]}
            value={fields[key] ?? ''}
            error={fieldError(key)}
            onChange={(value) => setFields((current) => ({ ...current, [key]: value }))}
          />
        ))}

        {save.error instanceof ApiError && Object.keys(save.error.fields).length === 0 ? (
          <p className="alert" role="alert">
            {save.error.message}
          </p>
        ) : null}

        <div className="row">
          <button className="btn" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {save.isSuccess && !save.isPending ? (
            <span className="muted" role="status">
              Saved.
            </span>
          ) : null}
        </div>

        {settings.updatedAt ? (
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Aakhri badlaav {formatDateTime(settings.updatedAt)}
            {settings.updatedByName ? ` · ${settings.updatedByName}` : ''}
          </p>
        ) : null}
      </form>
    </section>
  );
}

function Field({
  name,
  label,
  value,
  error,
  onChange,
}: {
  name: string;
  label: string;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={name}>{label}</label>
      <input id={name} value={value} onChange={(event) => onChange(event.target.value)} />
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/* --- the team -------------------------------------------------------------- */

function Team({ currentUserId }: { currentUserId: string }) {
  const { data, isPending, error } = useTeam(true);
  const invite = useInvite();
  const reinvite = useReinvite();
  const update = useUpdateTeamMember();
  /* The roles come from the database since 1.4a, so a role the owner created
     on /settings/roles has to be assignable here — a custom role nobody can
     be put on is a row and nothing else. Same owner-only surface, so this
     costs no extra permission. */
  const roles = useAssignableRoles();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AdminRole>('staff');

  /* The link is shown once, here, and never stored. Kept in component state
     rather than anywhere durable for that reason. */
  const link = invite.data?.inviteUrl ?? reinvite.data?.inviteUrl ?? null;

  function onInvite(event: FormEvent) {
    event.preventDefault();
    invite.mutate(
      { email: email.trim(), name: name.trim(), role },
      {
        onSuccess: () => {
          setEmail('');
          setName('');
        },
      },
    );
  }

  return (
    <section className="card">
      <h2 className="section-title">Team</h2>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the team.'}
        </p>
      ) : null}
      {isPending ? <p className="muted">Loading…</p> : null}

      {data ? (
        <div className="stack">
          {data.items.map((member) => (
            <Member
              key={member.id}
              member={member}
              roles={roles}
              isYou={member.id === currentUserId}
              onReinvite={() => reinvite.mutate(member.id)}
              onToggle={() =>
                update.mutate({
                  id: member.id,
                  changes: { disabled: !member.disabledAt },
                })
              }
              onRole={(role) => update.mutate({ id: member.id, changes: { role } })}
              busy={update.isPending || reinvite.isPending}
            />
          ))}
        </div>
      ) : null}

      <Problem error={update.error} />

      {link ? (
        <div className="alert alert--ok" role="status" style={{ marginTop: 12 }}>
          <strong>Send them this link — it is not shown again.</strong>
          <p style={{ margin: '6px 0 0', wordBreak: 'break-all', fontSize: 13 }}>{link}</p>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
            It expires in 7 days. They set their own password with it — you never see what they
            choose.
          </p>
        </div>
      ) : null}

      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '20px 0' }} />

      <form className="stack" onSubmit={onInvite} noValidate>
        <h3 style={{ fontSize: 16, margin: 0 }}>Add a team member</h3>

        <div className="field">
          {/* "Unka", not "Email": the account section above already has a
              field labelled Email, and two inputs with the same label on one
              page is ambiguous to a screen reader as well as to a test. */}
          <label htmlFor="inviteEmail">Their email</label>
          <input
            id="inviteEmail"
            type="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {invite.error instanceof ApiError && invite.error.fields.email ? (
            <span className="field__error" role="alert">
              {invite.error.fields.email}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="inviteName">Their name</label>
          <input
            id="inviteName"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="inviteRole">Role</label>
          <select
            id="inviteRole"
            value={role}
            onChange={(event) => setRole(event.target.value as AdminRole)}
          >
            {roles.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          {/* What a role may do is editable since 1.4, so this no longer
              describes it — describing it here would be a second copy that
              goes stale the first time the matrix is saved. */}
          <span className="muted" style={{ fontSize: 13 }}>
            Owner can do everything. What the other roles may do is on{' '}
            <Link to="/settings/roles">Roles</Link>, where you can change it. Raising an invoice,
            changing a care plan, everyone&rsquo;s login history and this screen stay owner-only
            whichever role you pick.
          </span>
        </div>

        {invite.error instanceof ApiError && Object.keys(invite.error.fields).length === 0 ? (
          <p className="alert" role="alert">
            {invite.error.message}
          </p>
        ) : null}

        <div>
          <button className="btn" type="submit" disabled={invite.isPending || !email.trim()}>
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </section>
  );
}

/** A role, as a `<select>` needs it. */
interface RoleOption {
  key: string;
  label: string;
}

/**
 * Every role that can be assigned, newest ones included.
 *
 * **It falls back to the six built-ins when the matrix cannot be read**, and
 * that is the important half: an empty `<select>` is an invite form that
 * cannot be submitted, and the six always exist — they are the rows the
 * migration seeded and the only ones that cannot be deleted. So a failed
 * request here costs the custom roles, not the screen.
 */
function useAssignableRoles(): RoleOption[] {
  const matrix = useRoleMatrix();
  if (!matrix.data?.roles) {
    return ADMIN_ROLES.map((key) => ({ key, label: ADMIN_ROLE_LABELS[key] }));
  }
  return sortRoles(matrix.data.roles).map((role) => ({ key: role.key, label: role.label }));
}

/**
 * A role's name on screen.
 *
 * The six built-ins have their labels in code; a role the owner created has
 * its own `Role.label` in the database. Showing the key is worse than showing
 * a label and better than showing `undefined`, which is what indexing the
 * built-in map with an arbitrary string would have produced.
 */
function roleLabel(key: string, roles: RoleOption[]): string {
  return (
    roles.find((role) => role.key === key)?.label ??
    (ADMIN_ROLE_LABELS as Record<string, string | undefined>)[key] ??
    key
  );
}

function Member({
  member,
  roles,
  isYou,
  onReinvite,
  onToggle,
  onRole,
  busy,
}: {
  member: AdminUserDto;
  roles: RoleOption[];
  isYou: boolean;
  onReinvite: () => void;
  onToggle: () => void;
  onRole: (role: AdminRole) => void;
  busy: boolean;
}) {
  const state = accountState(member);

  return (
    <div className="lead-card">
      <div className="lead-card__business">
        {member.name ?? member.email}
        {isYou ? ' (you)' : ''}
      </div>
      <div className="lead-card__meta">
        {member.email} · {roleLabel(member.role, roles)} ·{' '}
        <span className={state === 'active' ? 'pill' : 'pill pill--muted'}>
          {ACCOUNT_STATE_LABELS[state]}
        </span>
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        {state === 'invited' ? (
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={onReinvite}
            disabled={busy}
          >
            Make a new link
          </button>
        ) : null}

        {/* Absent for yourself, like the button below and for the same
            reason: the API refuses taking your own owner role away (it is the
            screen you would undo it from), so offering the control would be
            offering a 409. */}
        {isYou ? null : (
          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13 }}>
              Role
            </span>
            <select
              aria-label={`Role for ${member.email}`}
              value={member.role}
              disabled={busy}
              onChange={(event) => onRole(event.target.value as AdminRole)}
            >
              {roles.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {isYou ? null : (
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={onToggle}
            disabled={busy}
          >
            {member.disabledAt ? 'Re-enable' : 'Disable'}
          </button>
        )}
      </div>
    </div>
  );
}

/** One error line, for the mutations whose failures are not per-field. */
function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="alert" role="alert">
      {error instanceof ApiError ? error.message : 'That did not work.'}
    </p>
  );
}
